import { inject, Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BOGOF_STORE_API_ACTIONS } from '@store/bogof/bogof.action';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import { statedDataToAction } from '@shared/utils/rxjs/stated-data-to-action';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { BogofApiService } from '@core/services/api/bogof/bogof-api.service';
import { EMPTY } from 'rxjs';
import { DoodCartModel } from '@core/models/cart.model';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrdersService } from '@core/services/orders/orders.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { BogofModalModalConfig } from '@shared/modals/bogof-modal/bogof-modal.component';
import { CART_ACTIONS } from '@store/cart';
import { DoodProductModel } from '@core/models/product.model';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { Store } from '@ngrx/store';
import { selectEligiblePromotionsData } from '@store/eligible-promotions/eligible-promotions.selector';
import {
  EligiblePromotion,
  getEligiblePromotionProductId,
} from '@core/services/api/bogof/bogof-api.service';

@Injectable({
  providedIn: 'root',
})
export class BogofEffects {
  private readonly actions$ = inject(Actions);
  private readonly bogofApiService = inject(BogofApiService);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly ordersService = inject(OrdersService);
  private readonly modalStoreSelector = inject(ModalStoreSelector);
  private readonly store = inject(Store);
  private readonly modalsService: ModalsService<BogofModalModalConfig> = inject(
    ModalsService<BogofModalModalConfig>,
  );

  _loadBogofEffect = createEffect(() => {
    return this.actions$.pipe(
      ofType(CART_ACTIONS.updateProductInCart),
      filter(({ action }) => action === 'add' || action === 'increase'),
      // The gift line is itself an eligible product. Opening the offer again would loop.
      filter(({ cartItem }) => cartItem.origin?.channel !== 'bogof'),
      filter(({ productId }) => this.isProductEligibleForBogof(productId)),
      tap(({ productId }) => {
        this.modalsService.open('bogof-modal', undefined, {
          triggerProductId: productId,
          promotionName: this.resolveEligibleBogofPromotionTitle(productId) ?? '',
          products: [],
          loading: true,
        });
      }),
      switchMap(({ productId, productShopId }) => {
        const cart = this.cartStoreSelector.selectActive();
        const shop = this.shopStoreSelector.selectActive();

        if (!shop) {
          return EMPTY;
        }

        const order = this.ordersService.mapCartToOrderValues(cart as DoodCartModel, []);

        return statedDataToAction(
          statedStream(
            this.bogofApiService
              .getBogofSuggestions$({
                trigger_product_id: productId,
                trigger_product_shop_id: productShopId ?? shop.id,
                order,
              })
              .pipe(map(list => list ?? [])),
          ),
          {
            loading: () => BOGOF_STORE_API_ACTIONS.loadBogofLoading(),
            failure: (error: DoodApiError) => BOGOF_STORE_API_ACTIONS.loadBogofFailure({ error }),
            success: data =>
              BOGOF_STORE_API_ACTIONS.loadBogofSuccess({
                triggerProductId: productId,
                suggestions: data ?? [],
              }),
          },
        );
      }),
    );
  });

  _openBogofModalWhenSuggestionsLoadedEffect = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BOGOF_STORE_API_ACTIONS.loadBogofSuccess),
        tap(({ triggerProductId, suggestions }) => {
          if (!this.isBogofModalOpen()) return;
          if ((suggestions?.length ?? 0) < 1) {
            this.modalsService.close('bogof-modal');
            return;
          }

          const shop = this.shopStoreSelector.selectActive();
          if (!shop || !suggestions?.length) return;

          const firstSuggestion = suggestions[0];
          const products = resolveFreeProductsToModels(
            firstSuggestion.free_products,
            shop.products ?? [],
          );

          const triggerProduct = shop.products?.find(
            (p: { id: string; type?: string; additions?: { products?: unknown[] } }) =>
              p.id === triggerProductId,
          );

          if (!triggerProduct) return;

          const isNonComposableProduct =
            triggerProduct.type === 'SIMPLE' &&
            (!triggerProduct.additions?.products || triggerProduct.additions.products.length === 0);
          const lastEditedCartItem = this.cartStoreSelector.lastEditedCartItem();

          if (isNonComposableProduct) {
            this.modalsService.setData('bogof-modal', {
              triggerProductId,
              promotionName: firstSuggestion.promotion_name,
              products,
              loading: false,
              modalMode: 'products',
              triggerProduct,
              cartItemToDuplicate: lastEditedCartItem ?? undefined,
              isComposition: !isNonComposableProduct,
              productShopId: shop.id,
            });
            return;
          }

          // !isNonComposableProduct && hasSingleProposition
          // const hasSingleProposition = suggestions.length === 1;
          const hasSingleProposition = firstSuggestion.free_products?.length === 1;
          const freeProduct = firstSuggestion.free_products?.[0];

          if (hasSingleProposition && freeProduct.id === triggerProductId) {
            this.modalsService.setData('bogof-modal', {
              promotionName: firstSuggestion.promotion_name,
              triggerProductId,
              products,
              loading: false,
              modalMode: 'same-product',
              cartItemToDuplicate: lastEditedCartItem ?? undefined,
              isComposition: !isNonComposableProduct,
              triggerProduct,
            });
            return;
          }

          const freeProductsIncludesTrigger = firstSuggestion.free_products?.some(
            fp => fp.id === triggerProductId,
          );
          const hasDifferentFreeProduct = firstSuggestion.free_products?.find(
            fp => fp.id !== triggerProductId,
          );

          // composition (burger), proposition > 1 (BOGOF 1 boisson ou 1 dessert MAIS pas le même produit) => bogof-modal
          const sameProductInMultiplePropositions =
            !hasSingleProposition && freeProductsIncludesTrigger;
          if (hasDifferentFreeProduct && !sameProductInMultiplePropositions) {
            this.modalsService.setData('bogof-modal', {
              triggerProductId,
              promotionName: firstSuggestion.promotion_name,
              products,
              loading: false,
              modalMode: 'products',
              triggerProduct,
              cartItemToDuplicate: lastEditedCartItem ?? undefined,
              isComposition: !isNonComposableProduct,
              productShopId: shop.id,
            });
            return;
          }

          // composition (burger), proposition > 1 qui inclue le même produit => bogof-modal-same-but-different-product
          if (sameProductInMultiplePropositions) {
            this.modalsService.setData('bogof-modal', {
              triggerProductId,
              productShopId: shop.id,
              promotionName: firstSuggestion.promotion_name,
              products,
              loading: false,
              modalMode: 'same-but-different',
              cartItemToDuplicate: lastEditedCartItem ?? undefined,
              triggerProduct,
              isComposition: !isNonComposableProduct,
            });
          }
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  _closeBogofLoadingModalOnFailureEffect = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BOGOF_STORE_API_ACTIONS.loadBogofFailure),
        tap(() => {
          if (this.isBogofModalOpen()) {
            this.modalsService.close('bogof-modal');
          }
        }),
      );
    },
    { dispatch: false },
  );

  private isBogofModalOpen(): boolean {
    return this.modalStoreSelector.modals.some(modal => modal.handle === 'bogof-modal');
  }

  private isProductEligibleForBogof(productId: string): boolean {
    const promotions = this.store.selectSignal(selectEligiblePromotionsData)();
    if (!promotions?.length) return false;

    return promotions.some(
      (promotion: EligiblePromotion) =>
        promotion.type === 'buy-x-get-y-free' &&
        (promotion.products ?? []).some(p => getEligiblePromotionProductId(p) === productId),
    );
  }

  private resolveEligibleBogofPromotionTitle(triggerProductId: string): string | undefined {
    const promotions = this.store.selectSignal(selectEligiblePromotionsData)();
    if (!promotions?.length) return undefined;

    const bogofPromotion = promotions.find((promotion: EligiblePromotion) => {
      if (promotion.type !== 'buy-x-get-y-free') return false;
      return (promotion.products ?? []).some(
        p => getEligiblePromotionProductId(p) === triggerProductId,
      );
    });

    return bogofPromotion?.title;
  }
}

function resolveFreeProductsToModels(
  freeProducts: { id: string }[],
  shopProducts: DoodProductModel[],
): DoodProductModel[] {
  return freeProducts
    .map(fp => shopProducts.find(p => p.id === fp.id))
    .filter((p): p is DoodProductModel => p != null);
}
